import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
	AlertMessage,
	Navigation,
	QuizTicketCard,
	QuizTicketPanorama,
	ITicketQuiz,
} from '@quizzach/components';
import { ApiFailedReason, RoutePath } from '@quizzach/interfaces';
import { useCartContext, useWindowListener } from '@quizzach/contexts';
import { useOrder } from '@quizzach/api-hooks';
import { scrollToTop } from '@quizzach/utils';
import {
	FinishAlertWrap,
	FinishHead,
	FinishPaymentPage,
	PanGrid,
	PanList,
	Sk,
	VsFinishGifts,
	breakpoints,
} from '@quizzach/styles';
import { Reveal } from '@quizzach/motion';
import { GiftCardStack } from './voucher-shop/GiftCards';

const KNOWN_TYPES = ['premade', 'quick', 'generator'];
const PHONE_WIDTH = 768;

export const OrderFinishPayment = () => {
	const { t } = useTranslation();
	const { touchDevice, width } = useWindowListener();
	const { getOrder, order, loading, error } = useOrder();
	const { uuid } = useParams<{ uuid: string }>();
	const { refreshCartCount } = useCartContext();

	// the panorama rows need desktop room: below the shell breakpoint the
	// bought quizzes fall back to the poster grid — same as /profile
	const isDesktop = width >= breakpoints.smallDesktop;

	useEffect(() => {
		scrollToTop();
		// returning from the payment provider - the cart state changed server-side
		refreshCartCount();
		if (uuid) {
			getOrder(uuid);
		}
	}, []);

	const skeletons = (className: string, count: number) =>
		Array.from({ length: count }).map((_, index) => (
			<Sk key={index} className={className} aria-hidden='true' />
		));

	if (loading) {
		return (
			<FinishPaymentPage>
				<Navigation />
				<FinishHead />
				{isDesktop ? (
					<PanList>{skeletons('pan-sk', 2)}</PanList>
				) : (
					<PanGrid>{skeletons('pan-card-sk', 2)}</PanGrid>
				)}
			</FinishPaymentPage>
		);
	}

	if (error || !order || !order.cart_items) {
		return (
			<FinishPaymentPage>
				<Navigation />
				<FinishAlertWrap>
					<AlertMessage code={ApiFailedReason.default} type='error' />
				</FinishAlertWrap>
			</FinishPaymentPage>
		);
	}

	const tickets: ITicketQuiz[] = order.cart_items
		// a gift has no quiz in the buyer's profile: its code goes out by mail
		.filter((item) => KNOWN_TYPES.includes(item.quiz_type) && !item.gift)
		.map((item) => ({
			uuid: item.created_quiz.uuid,
			quiz_type: item.quiz_type,
			created_at: +item.created_quiz.created_at,
			number_of_questions: item.created_quiz.number_of_questions,
			fresh: item.created_quiz.fresh,
			premade_quiz: item.quiz_type === 'premade' ? item.premade_quiz : null,
		}));

	// the headline is about what's ready here: the buyer's own quizzes, or the
	// gifts when the order holds nothing else
	const giftCount = order.cart_items.filter((item) => item.gift).length;
	const hasGifts = giftCount > 0;
	const onlyGifts = hasGifts && tickets.length === 0;
	const isPlural = (onlyGifts ? giftCount : tickets.length) > 1;
	const titles = onlyGifts ? 'voucherShop.finish' : 'common.orderFinish';

	const title = touchDevice ? (
		<>{isPlural ? t(`${titles}.titlePlural`) : t(`${titles}.titleSingular`)}</>
	) : isPlural ? (
		<>
			{t(`${titles}.titlePluralLine1`)} <br /> {t(`${titles}.titlePluralLine2`)}
		</>
	) : (
		<>
			{t(`${titles}.titleSingularLine1`)} <br />{' '}
			{t(`${titles}.titleSingularLine2`)}
		</>
	);

	const finishHead = (
		<FinishHead>
			<Reveal as='span' className='hand'>
				ta daa!
			</Reveal>
			<Reveal as='h1' delay={1}>
				{title}
			</Reveal>
			{!onlyGifts && (
				<Reveal as='p' delay={2}>
					{isPlural
						? t('common.orderFinish.explanationPluralPrefix')
						: t('common.orderFinish.explanationSingularPrefix')}{' '}
					<Link to={RoutePath.Profile}>
						{t('common.orderFinish.profileLink')}
					</Link>{' '}
					{t('common.orderFinish.explanationSuffix')}
				</Reveal>
			)}
			{hasGifts && (
				<Reveal as='p' delay={2}>
					{giftCount > 1
						? t('voucherShop.orderFinishPlural')
						: t('voucherShop.orderFinish')}
				</Reveal>
			)}
		</FinishHead>
	);

	return (
		<FinishPaymentPage>
			<Navigation />

			{onlyGifts ? (
				<VsFinishGifts>
					{finishHead}
					<Reveal className='cards' delay={2}>
						<GiftCardStack phone={width < PHONE_WIDTH} />
					</Reveal>
				</VsFinishGifts>
			) : (
				finishHead
			)}

			{isDesktop ? (
				<PanList>
					{tickets.map((quiz) => (
						<QuizTicketPanorama key={quiz.uuid} quiz={quiz} />
					))}
				</PanList>
			) : (
				<PanGrid>
					{tickets.map((quiz) => (
						<QuizTicketCard key={quiz.uuid} quiz={quiz} />
					))}
				</PanGrid>
			)}
		</FinishPaymentPage>
	);
};
